import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  inject,
  signal,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../environments/environment";

const GREETING =
  "Hi! I'd like to know more about planning a trip to Sri Lanka.";

@Component({
  selector: "app-whatsapp-float",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <a
      [href]="href"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      class="wa-fab"
      [class.wa-fab-in]="mounted()"
      [class.wa-fab-expanded]="autoOpen() || hovering()"
      (mouseenter)="hovering.set(true)"
      (mouseleave)="hovering.set(false)"
      (focus)="hovering.set(true)"
      (blur)="hovering.set(false)"
    >
      <span class="wa-ring" aria-hidden="true"></span>
      <span class="wa-label">Chat with us</span>
      <span class="wa-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.78-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35Z"
          />
          <path
            d="M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.85 9.85 0 0 1-1.51-5.26C2.17 6.44 6.6 2 12.06 2c2.63 0 5.11 1.03 6.98 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.9-9.88 9.9Zm8.41-18.3A11.8 11.8 0 0 0 12.05 0C5.5 0 .16 5.33.15 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.9-11.9a11.82 11.82 0 0 0-3.49-8.41Z"
          />
        </svg>
      </span>
    </a>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      .wa-fab {
        position: fixed;
        right: 1.25rem;
        bottom: 1.25rem;
        z-index: 80;
        display: flex;
        align-items: center;
        height: 3.5rem;
        border-radius: 999px;
        background: #25d366;
        color: #fff;
        box-shadow:
          0 10px 24px -8px rgba(22, 19, 15, 0.4),
          0 2px 6px rgba(22, 19, 15, 0.15);
        text-decoration: none;
        overflow: hidden;
        opacity: 0;
        transform: translateY(14px) scale(0.9);
        transition:
          transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
          opacity 0.55s ease,
          box-shadow 0.3s ease;
      }
      .wa-fab:hover,
      .wa-fab:focus-visible {
        box-shadow:
          0 14px 30px -8px rgba(22, 19, 15, 0.45),
          0 2px 8px rgba(22, 19, 15, 0.18);
      }
      .wa-fab-in {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      @media (min-width: 640px) {
        .wa-fab {
          right: 2rem;
          bottom: 2rem;
        }
      }

      .wa-ring {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: #25d366;
        opacity: 0;
        animation: wa-breathe 3.6s ease-in-out infinite;
        animation-delay: 1.4s;
      }
      @keyframes wa-breathe {
        0%,
        100% {
          transform: scale(1);
          opacity: 0.35;
        }
        50% {
          transform: scale(1.35);
          opacity: 0;
        }
      }

      .wa-label {
        position: relative;
        font-family: var(--font-sans);
        font-size: 0.875rem;
        font-weight: 600;
        white-space: nowrap;
        max-width: 0;
        opacity: 0;
        padding-left: 0;
        transition:
          max-width 0.4s cubic-bezier(0.22, 1, 0.36, 1),
          opacity 0.3s ease,
          padding-left 0.4s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .wa-fab-expanded .wa-label {
        max-width: 10rem;
        opacity: 1;
        padding-left: 1.1rem;
      }

      .wa-icon {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3.5rem;
        height: 3.5rem;
        flex-shrink: 0;
      }
      .wa-icon svg {
        width: 1.7rem;
        height: 1.7rem;
      }
      .wa-fab-expanded .wa-icon {
        padding-right: 0.15rem;
      }

      @media (prefers-reduced-motion: reduce) {
        .wa-fab {
          transition: opacity 0.3s ease;
          transform: none;
        }
        .wa-ring {
          animation: none;
          display: none;
        }
      }
    `,
  ],
})
export class WhatsappFloat {
  private platformId = inject(PLATFORM_ID);

  href = `https://wa.me/${environment.whatsappNumber}?text=${encodeURIComponent(GREETING)}`;
  mounted = signal(false);
  hovering = signal(false);
  autoOpen = signal(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    setTimeout(() => this.mounted.set(true), 600);

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) return;

    setTimeout(() => this.autoOpen.set(true), 1400);
    setTimeout(() => this.autoOpen.set(false), 4800);
  }
}
